import { anyReallyAny } from "@cervval.js/common/tools";
import { SmartTableColumn } from "../smartTableColumn.tools";
import { CopyPasteHandler } from "./smartTableCopyPaste.types";

/** Default handler to manage copy paste of text/plain type */
export const DEFAULT_TEXT_PLAIN_COPY_PASTE_HANDLER: CopyPasteHandler<anyReallyAny> = {
    format: "text/plain",
    convertToClipboardString: (event, cell) => {
        return cell.stringValue ?? (cell.value == null ? null : "" + cell.value);
    },
    convertFromClipboardString: (value) => {
        return value;
    },
    /** Try to split values sent as text from Excel */
    preSplitValue: (value) => {
        const values = [];
        // TODO parse with quote handling (use csvparse node module ?)
        const lines = String(value).replace(new RegExp("\\r", "g"), "").split("\n");
        // If last element of the array is empty, delete it (will prevent the creation of an additionnal empty cell)
        if (lines.length > 0 && lines[lines.length - 1] === "") { lines.pop(); }

        for (let l = 0; l < lines.length; l++) {
            const lineCells: string[] = [];
            values[l] = lineCells;
            const cols = (lines[l] ?? "").split("\t");
            for (let c = 0; c < cols.length; c++) {
                const cellValue = cols[c];
                lineCells[c] = cellValue;
            }
        }
        return values;
    }
};

/** Default handler to manage copy paste of text/html type */
export const DEFAULT_TEXT_HTML_COPY_PASTE_HANDLER: CopyPasteHandler<anyReallyAny> = {
    format: "text/html",
    convertToClipboardString: (event, cell) => {
        if (cell.column.renderCell == null) { return null; }
        const renderCell = cell.column.renderCell(cell);
        return renderCell?.toString() ?? null;
    },
    convertFromClipboardString: (value) => {
        return value;
    }
};

const UNDEFINED_COPY_VALUE = "Undefined_copy";
/** Default handler to manage copy paste of application/smartTable type */
export const DEFAULT_TEXT_INTERNAL_APPLICATION_COPY_PASTER_HANDLER: CopyPasteHandler<anyReallyAny> = {
    format: "application/smartTable",
    convertToClipboardString: (event, cell) => {
        if (cell.value == null) { return UNDEFINED_COPY_VALUE; }
        return JSON.stringify(cell.value);
    },
    convertFromClipboardString: (value) => {
        if (value == null || value === UNDEFINED_COPY_VALUE) { return [[undefined]]; }
        return JSON.parse(value);
    }
};

/** 
 * Create a copy/paste handler for a column for application/smartTable type.      
 * Make the format to application/smartTable_{ColumnUid} to avoid to paste data with incompatible type in column
 */
export function buildAppSTCopyPasteHandler<ItemData>(column: SmartTableColumn<ItemData>): CopyPasteHandler<ItemData> {
    return {
        ...DEFAULT_TEXT_INTERNAL_APPLICATION_COPY_PASTER_HANDLER,
        format: `application/smartTable_${column.uid}`
    };
}

/** 
 * Default copy/paste handlers used for column, in order application/smartTable, text/plain and text/html.    
 * Use {@link buildAppSTCopyPasteHandler} to create application/smartTable handler
 */
export function getDefaultCopyPasteHandlers<ItemData>(column: SmartTableColumn<ItemData>): CopyPasteHandler<ItemData>[] {
    return [buildAppSTCopyPasteHandler(column),
        DEFAULT_TEXT_PLAIN_COPY_PASTE_HANDLER, DEFAULT_TEXT_HTML_COPY_PASTE_HANDLER];
}